<script setup>
import { computed, ref, useTemplateRef, watch } from "vue";
import {
  VueStripeElements,
  VueStripeExpressCheckoutElement,
  VueStripePaymentElement,
  VueStripeProvider,
} from "@vue-stripe/vue-stripe";
import AlertError from "./AlertError.vue";
import { useStripeLoadWarning } from "../helpers/stripe-loading";

const props = defineProps({
  amountCents: {
    type: Number,
    required: true,
  },
  config: {
    type: Object,
    default: () => ({}),
  },
  customer: {
    type: Object,
    default: () => ({}),
  },
  saving: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["payment", "errors"]);
const stripeLoaded = ref(false);
const stripeInstance = ref(null);
const cardElements = ref(null);
const expressElements = ref(null);
const expressMethodsAvailable = ref(false);
const error = ref("");
const cardElementsComponent = useTemplateRef("cardElementsComponent");
const expressElementsComponent = useTemplateRef("expressElementsComponent");

const elementsOptions = computed(() => ({
  mode: "payment",
  amount: Math.max(1, props.amountCents),
  currency: "aud",
  loader: "always",
}));

const expressOptions = {
  paymentMethods: {
    applePay: "auto",
    googlePay: "auto",
    link: "never",
  },
  paymentMethodOrder: ["apple_pay", "google_pay"],
  emailRequired: true,
  phoneNumberRequired: true,
  billingAddressRequired: true,
};

const paymentElementOptions = {
  paymentMethodOrder: ["card"],
  layout: {
    type: "tabs",
  },
};

const setError = (message = "") => {
  error.value = message;
  emit("errors", message ? [message] : []);
};

const {
  clearStripeLoadWarning,
  showStripeLoadWarning,
} = useStripeLoadWarning({
  stripeLoaded,
  emit,
  setError,
});

const onStripeLoad = (stripe) => {
  clearStripeLoadWarning();
  stripeInstance.value = stripe;
  stripeLoaded.value = true;
  setError();
};

const onCardElementsReady = (elements) => {
  cardElements.value = elements;
};

const onExpressElementsReady = (elements) => {
  expressElements.value = elements;
};

const onExpressReady = (event) => {
  expressMethodsAvailable.value = Object.values(event?.availablePaymentMethods ?? {})
    .some((available) => available === true);

  if (event?.error) {
    setError(event.error.message);
  }
};

const createToken = async (elements, includeBillingDetails = false) => {
  if (!stripeInstance.value || !elements) {
    setError("Please wait for the secure payment form to finish loading.");
    return null;
  }

  const params = {
    return_url: window.location.href,
  };

  if (includeBillingDetails) {
    params.payment_method_data = {
      billing_details: {
        name: [props.customer?.firstName, props.customer?.lastName].filter(Boolean).join(" "),
        email: props.customer?.email ?? undefined,
        phone: props.customer?.phone ?? undefined,
      },
    };
  }

  const result = await stripeInstance.value.createConfirmationToken({
    elements,
    params,
  });

  if (result.error) {
    setError(result.error.message);
    return null;
  }

  return result.confirmationToken;
};

const submitCard = async () => {
  if (props.saving || !cardElements.value) {
    return;
  }

  cardElements.value.update({ amount: Math.max(1, props.amountCents) });
  const result = await cardElements.value.submit();

  if (result?.error) {
    setError(result.error.message);
    return;
  }

  const confirmationToken = await createToken(cardElements.value, true);
  if (confirmationToken) {
    emit("payment", confirmationToken);
  }
};

const handleExpressClick = (event) => {
  if (props.saving || !expressElements.value) {
    event.reject();
    return;
  }

  expressElements.value.update({ amount: Math.max(1, props.amountCents) });
  event.resolve();
};

const handleExpressConfirm = async () => {
  const confirmationToken = await createToken(expressElements.value);

  if (confirmationToken) {
    emit("payment", confirmationToken);
  }
};

const handleServerResponse = async (data) => {
  if (!data?.requires_action || data?.intent?.status !== "requires_action") {
    return null;
  }

  if (!stripeInstance.value) {
    setError("The secure payment connection is not ready. Please try again.");
    return null;
  }

  const { error: stripeError, paymentIntent } = await stripeInstance.value.handleNextAction({
    clientSecret: data.intent.client_secret,
  });

  if (stripeError) {
    setError(stripeError.message);
    return null;
  }

  return paymentIntent?.id ?? null;
};

watch(() => props.amountCents, (amount) => {
  const update = { amount: Math.max(1, amount) };
  cardElements.value?.update?.(update);
  expressElements.value?.update?.(update);
});

defineExpose({
  handleServerResponse,
});
</script>

<template>
  <div class="rounded-[28px] bg-light px-5 py-7 lg:px-8 lg:py-8">
    <VueStripeProvider
      :publishable-key="config.token"
      @load="onStripeLoad"
      @error="showStripeLoadWarning"
    >
      <div v-if="!stripeLoaded" class="rounded-2xl bg-white px-5 py-8 text-center text-dark/60">
        Loading secure payment options...
      </div>

      <template v-else>
        <VueStripeElements
          :options="elementsOptions"
          ref="expressElementsComponent"
          @ready="onExpressElementsReady"
          @error="showStripeLoadWarning"
        >
          <VueStripeExpressCheckoutElement
            :options="expressOptions"
            @ready="onExpressReady"
            @click="handleExpressClick"
            @confirm="handleExpressConfirm"
          />
        </VueStripeElements>

        <div v-if="expressMethodsAvailable" class="relative flex items-center py-6">
          <div class="flex-grow border-t border-dark/20"></div>
          <span class="mx-4 text-sm font-semibold uppercase text-dark/60">Or pay by card</span>
          <div class="flex-grow border-t border-dark/20"></div>
        </div>

        <div class="rounded-2xl bg-white p-5">
          <div class="mb-4 flex items-center justify-between gap-4">
            <h3 class="text-xl font-bold">Secure payment</h3>
            <img
              :src="`${$settings.theme_url}/assets/images/cards.png`"
              class="h-4 w-auto"
              alt="Visa, Mastercard and American Express"
            >
          </div>

          <VueStripeElements
            :options="elementsOptions"
            ref="cardElementsComponent"
            @ready="onCardElementsReady"
            @error="showStripeLoadWarning"
          >
            <VueStripePaymentElement
              :options="paymentElementOptions"
              @change="setError()"
            />
          </VueStripeElements>

          <button
            type="button"
            class="mt-6 inline-flex w-full items-center justify-center rounded-full bg-primary px-6 py-3 font-bold text-dark disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="saving || !cardElements"
            @click="submitCard"
          >
            {{ saving ? "Processing..." : `Pay AUD $${(amountCents / 100).toFixed(2)} and move booking` }}
          </button>
        </div>
      </template>
    </VueStripeProvider>

    <AlertError v-if="error" class="mt-4">
      {{ error }}
    </AlertError>
  </div>
</template>
